<template>
    <b-modal
        id="brand-to-generic-prompt-modal"
        title="Generic Available"
        size="lg"
        no-close-on-backdrop
        no-close-on-esc
        :hide-footer="!keepBrandExpanded"
        :ok-disabled="!canSubmitKeepBrand || isSubmitting"
        ok-title="Save Brand Anyway"
        ok-variant="warning"
        cancel-title="Cancel Save"
        cancel-variant="outline-secondary"
        @ok="handleKeepBrand"
        @hidden="onHidden"
    >
        <b-alert show variant="warning" class="mb-3">
            <i class="fas fa-exclamation-triangle mr-1"></i>
            <strong>{{ brandDrugName || "This brand-name drug" }}</strong>
            has an equivalent generic available. Generics are
            typically cheaper for the pharmacy and the patient.
        </b-alert>

        <template>
            <h6 class="mb-2">Substitute with a generic — one click:</h6>
            <b-table
                small
                striped
                outlined
                responsive
                :items="genericAlternatives"
                :fields="genericFields"
            >
                <template #cell(onHand)="row">
                    <span v-if="isInStock(row.item)">{{ row.item.onHand }}</span>
                    <b-badge v-else variant="secondary" pill>Out of stock</b-badge>
                </template>
                <template #cell(action)="row">
                    <b-button
                        size="sm"
                        variant="success"
                        :disabled="isSubmitting"
                        @click="chooseGeneric(row.item)"
                    >
                        Use This Generic
                    </b-button>
                </template>
                <template #cell(lastPurchasePrice)="row">
                    {{ row.item.lastPurchasePrice != null ? formatCurrency(row.item.lastPurchasePrice) : "—" }}
                </template>
            </b-table>

            <hr class="my-3" />

            <b-button
                v-if="!keepBrandExpanded"
                block
                variant="outline-warning"
                :disabled="isSubmitting"
                @click="keepBrandExpanded = true"
            >
                Keep the Brand Instead…
            </b-button>

            <div v-else>
                <h6 class="mb-2 text-warning">
                    <i class="fas fa-exclamation-triangle mr-1"></i>
                    Keeping the brand requires a DAW code and pharmacist initials.
                </h6>

                <b-form-group label="DAW Code" :invalid-feedback="dawError" :state="dawState">
                    <b-form-select
                        v-model="selectedDawCode"
                        :options="dawOptions"
                        :disabled="isSubmitting"
                        :state="dawState"
                    />
                </b-form-group>

                <b-form-group
                    label="Pharmacist Initials"
                    :invalid-feedback="initialsError"
                    :state="initialsState"
                >
                    <b-form-input
                        v-model="pharmacistInitials"
                        maxlength="20"
                        :state="initialsState"
                        :disabled="isSubmitting"
                        :placeholder="initialsPlaceholder"
                    />
                </b-form-group>
            </div>
        </template>

        <template v-if="isSubmitting" #modal-ok>
            <b-spinner small class="mr-1" />Saving…
        </template>
    </b-modal>
</template>

<script lang="ts">
import { Component, Vue } from "vue-property-decorator";
import { Drug } from "@/models/Drug/Drug";
import { DAWCode } from "@/models/Prescription";
import TextUtils from "@/util/TextUtils";

export interface KeepBrandPayload {
    dawCode: DAWCode;
    pharmacistInitials: string;
    suggestedGenericPackageIds: number[];
}

@Component({ name: "BrandToGenericPromptModal" })
export default class BrandToGenericPromptModal extends Vue {
    // Inputs captured by show() rather than reactive props so the prompt is stable across
    // rapid parent re-renders. Equivalents are pre-fetched by the parent so the modal opens
    // populated and doesn't need a loading state.
    private brandPackageId: number | null = null;
    private brandDrugName: string | null = null;

    private isSubmitting = false;
    // True once the operator resolved the prompt (use-generic or keep-brand), so @hidden can tell
    // a resolution apart from a dismissal (Cancel / esc / backdrop) and emit "dismissed" only then.
    private resolved = false;
    private genericAlternatives: Drug.EquivalentDrug[] = [];
    private keepBrandExpanded = false;

    private selectedDawCode: DAWCode | null = null;
    private pharmacistInitials = "";

    private genericFields = [
        { key: "productNameLong", label: "Generic", sortable: true },
        { key: "marketer", label: "Marketer", sortable: true },
        { key: "onHand", label: "On Hand", sortable: true, class: "text-right" },
        { key: "lastPurchasePrice", label: "Last Purchase", sortable: true, class: "text-right" },
        { key: "action", label: "", class: "text-right" },
    ];

    private dawOptions = [
        { value: null, text: "— Select a DAW code —", disabled: true },
        { value: DAWCode.Prescriber, text: "1 — Prescriber requires brand" },
        { value: DAWCode.Patient, text: "2 — Patient requests brand" },
        { value: DAWCode.Pharmacy, text: "3 — Pharmacist selects brand" },
        { value: DAWCode.GenericNotStocked, text: "4 — Generic not stocked" },
        { value: DAWCode.Override, text: "6 — Override" },
        { value: DAWCode.SubstitutionNotAllowed, text: "7 — Substitution not allowed" },
        { value: DAWCode.Other, text: "9 — Other" },
    ];

    get initialsPlaceholder(): string {
        // Falls back to a neutral hint if the logged-in user has no initials configured.
        const initials = ((this as any).$user?.initials ?? "").trim();
        return initials ? `e.g. ${initials}` : "Pharmacist initials";
    }

    get dawState(): boolean | null {
        if (!this.keepBrandExpanded) return null;
        return this.selectedDawCode != null;
    }

    get dawError(): string {
        return this.selectedDawCode == null ? "DAW code is required to keep the brand." : "";
    }

    get initialsState(): boolean | null {
        if (!this.keepBrandExpanded) return null;
        return this.pharmacistInitials.trim().length > 0;
    }

    get initialsError(): string {
        return this.pharmacistInitials.trim().length === 0
            ? "Pharmacist initials are required to keep the brand." : "";
    }

    get canSubmitKeepBrand(): boolean {
        return this.keepBrandExpanded
            && this.selectedDawCode != null
            && this.pharmacistInitials.trim().length > 0;
    }

    formatCurrency(value: number): string {
        return TextUtils.currencyFormatter(value);
    }

    /**
     * Whether the generic has on-hand stock at the filling store. Used only to flag out-of-stock
     * rows visually — substitution is still allowed (the pharmacist may know stock is arriving).
     */
    private isInStock(item: Drug.EquivalentDrug): boolean {
        return (item.onHand ?? 0) > 0;
    }

    /**
     * Open the modal with the pre-fetched generic alternatives.
     * Caller is responsible for fetching the active generic equivalents (only call show()
     * when there's at least one) — the modal opens populated without doing its own HTTP fetch.
     */
    public show(brandPackageId: number, brandDrugName: string | null, genericAlternatives: Drug.EquivalentDrug[]): void {
        this.reset();
        this.brandPackageId = brandPackageId;
        this.brandDrugName = brandDrugName;
        this.genericAlternatives = genericAlternatives;
        this.$bvModal.show("brand-to-generic-prompt-modal");
    }

    private chooseGeneric(generic: Drug.EquivalentDrug) {
        // Double-click guard: chooseGeneric emits synchronously and the modal closes async,
        // so a fast second click could fire a second emission before the close lands. Setting
        // isSubmitting disables both the row buttons and the OK button, and reset() clears it
        // on @hidden so the next open starts fresh.
        if (this.isSubmitting) return;
        if (!generic.packageID) return;
        this.isSubmitting = true;
        this.resolved = true;
        this.$emit("use-generic", generic);
        this.$bvModal.hide("brand-to-generic-prompt-modal");
    }

    private handleKeepBrand(bvEvent: any) {
        // Block the modal close only when validation fails. On the success path we let the OK
        // event run its default close — calling preventDefault + $bvModal.hide back-to-back
        // races with bootstrap-vue's event handling and leaves the modal stuck open.
        if (!this.canSubmitKeepBrand) {
            bvEvent.preventDefault();
            return;
        }
        // Double-click guard: emit is synchronous; without this a fast second OK click would
        // re-emit "keep-brand" before the modal hides, causing duplicate saves/audit POSTs.
        if (this.isSubmitting) {
            bvEvent.preventDefault();
            return;
        }
        this.isSubmitting = true;
        this.resolved = true;

        const payload: KeepBrandPayload = {
            dawCode: this.selectedDawCode!,
            pharmacistInitials: this.pharmacistInitials.trim(),
            suggestedGenericPackageIds: this.genericAlternatives
                .map(g => g.packageID)
                .filter((id): id is number => id != null),
        };
        this.$emit("keep-brand", payload);
    }

    // b-modal @hidden fires for every close. Emit "dismissed" only when the operator closed the
    // prompt without resolving it (Cancel / esc / backdrop), so the container can drop the parked save.
    private onHidden() {
        if (!this.resolved) this.$emit("dismissed");
        this.reset();
    }

    private reset() {
        this.genericAlternatives = [];
        this.keepBrandExpanded = false;
        this.selectedDawCode = null;
        this.pharmacistInitials = "";
        this.isSubmitting = false;
        this.resolved = false;
    }
}
</script>
